import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  Button,
} from '../../../../components';

import MenuIcon from '../MenuIcon';
import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink className={css.topbarLink} name="InboxPage" params={{ tab: inboxTab }}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const farmerVendor = currentUser?.attributes?.profile?.publicData?.userType == "farmiez-farmer"
  const shopperVendor = currentUser?.attributes?.profile?.publicData?.userType == "farmiez-shopper"

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>

        {farmerVendor && (
          <MenuItem key="VendorDashboardPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('VendorDashboardPage'))}
              name="VendorDashboardPage"
            >
              <span className={css.menuItemBorder} />
              Vendor Dashboard
            </NamedLink>
          </MenuItem>
        )}
        {shopperVendor && (
          <MenuItem key="ShopperDashboardPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ShopperDashboardPage'))}
              name="ShopperDashboardPage"
            >
              <span className={css.menuItemBorder} />
              Shopper Dashboard
            </NamedLink>
          </MenuItem>
        )}
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};



const NavMenuLink = ( {farmiezFarmer, farmiezShopper} ) => {

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const toggleMenu = () => {
    setIsOpen(prev => !prev);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={css.dropdownWrapper} ref={menuRef}>
      <Button rootClassName={css.menuDesktop} onClick={toggleMenu}>
        <MenuIcon className={css.menuIcon} />
      </Button>

      {isOpen && (
        <div className={css.dropdownMenu}>
          <ul>
            <li>
              <NamedLink name="HowItWorksPage" className={css.coopButton}>How It Works</NamedLink>
            </li>
            <li>
              <NamedLink name="SubscriptionPlansPage" className={css.coopButton}>Plan & Pricing</NamedLink>
            </li>
            {farmiezFarmer && (
              <li>
                <NamedLink name="SignupPage" className={css.coopButton}>
                  <span>🐓</span> 
                  <span>Join the Coop (Farmer)</span>
                </NamedLink>
              </li>
            )}
            {farmiezShopper && (
              <li>
                <NamedLink name="SignupPage" className={css.coopButton}>
                  <span>🥚</span> 
                  <span>Shop Farmiez Now (Shopper)</span>
                </NamedLink>
              </li>
            )}
            <li>
              <NamedLink name="FAQPage" className={css.coopButton}>FAQ</NamedLink>
            </li>
            <li>
              <NamedLink name="AboutUsPage" className={css.coopButton}>About Us</NamedLink>
            </li>
            <li>
              <NamedLink name="OurStoryPage" className={css.coopButton}>Our Story</NamedLink>
            </li>
            <li>
              <NamedLink name="FarmiezFieldGuidesPage" className={css.coopButton}>Farmiez Field Guides</NamedLink>
            </li>
            <li>
              <NamedLink name="PrivacyPolicyPage" className={css.coopButton}>Privacy Policy</NamedLink>
            </li>
            <li>
              <NamedLink name="TermsOfServicePage" className={css.coopButton}>Terms of Service</NamedLink>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch }, css.searchBarWrap)}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );
  const farmiezFarmer = currentUser?.attributes?.profile?.publicData?.userType == "farmiez-farmer"
  const farmiezShopper = currentUser?.attributes?.profile?.publicData?.userType == "farmiez-shopper"
  return (
    <nav className={classes}>
      <NavMenuLink
        farmiezFarmer={farmiezFarmer}
        farmiezShopper={farmiezShopper}
      />
      
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />

      {searchFormMaybe}

      <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        showCreateListingsLink={showCreateListingsLink}
        farmiezFarmer={farmiezFarmer}
      />
      {farmiezFarmer && (
        <NamedLink name="SignupPage" className={css.farmerSignUpLink}>
          <span>Join the Coop (Farmer)</span>
        </NamedLink>
      )}
      {inboxLinkMaybe}
      {profileMenuMaybe}
      {signupLinkMaybe}
      {loginLinkMaybe}
    </nav>
  );
};

export default TopbarDesktop;
